import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { FetchSessionArg, PastChatState } from "../../assets/interfaces/interfaces";
import { updateLatestSummaryFromLLM } from "./newsSummarySlice";
import { PendingQueryResponseStatus } from "../../components/ChatMessage/constants";

const initialState: PastChatState = {
  pastMessages: [],
  status: "idle",
  error: null,
  pendingQueries: {},
  isCancellingQuery: false,
};

export const fetchPastSession = createAsyncThunk(
  "chat/fetchPastSession",
  async (arg: FetchSessionArg, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_SPL_URL}/tradegpt-session/${arg.sessionId}`, { withCredentials: true });
      const latestMessage = data
        .filter(message => message.news_summaries && message.news_summaries.length > 0)
        .sort((a, b) => new Date(b.created_at).valueOf() - new Date(a.created_at).valueOf())[0];

      if (latestMessage) {
        const { session_id, message_id, news_summaries } = latestMessage;

        dispatch(
          updateLatestSummaryFromLLM({
            [session_id]: { chat_id: message_id, news_summary: news_summaries },
          }),
        );
      }
      return data;
    }
    catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const fetchLatestMessage = createAsyncThunk(
  "chat/fetchLatestMessage",
  async ({ sessionId }: FetchSessionArg, { rejectWithValue }) => {
    try {
      const { data, status } = await axios.get(`${process.env.REACT_APP_SPL_URL}/tradegpt-session/${sessionId}`, { withCredentials: true });
      if (status === 304 || !Array.isArray(data) || data.length === 0) { // handle cases where no data is returned
        return null;
      }

      return data[data.length - 1];
    }
    catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const getPendingQuery = createAsyncThunk(
  "chat/getPendingQuery",
  async (arg: FetchSessionArg, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_SPL_URL}/v1/tradegpt/chat-sessions/${arg.sessionId}/stream-status`, { withCredentials: true });
      const data = response.data;
      if (data?.status === PendingQueryResponseStatus.COMPLETED) {
        return null;
      }
      return data;
    }
    catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const cancelChatResponse = createAsyncThunk(
  "chat/cancelChatResponse",
  async (arg: { sessionId: string; messageId: string }, { rejectWithValue }) => {
    try {
      await axios.post(`${process.env.REACT_APP_SPL_URL}/v1/tradegpt/chat-sessions/${arg.sessionId}/cancel`,
        { message_id: arg.messageId },
        { withCredentials: true });
    }
    catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPastSession.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchPastSession.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.pastMessages = action.payload;
      })
      .addCase(fetchPastSession.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to fetch messages";
      })
      .addCase(getPendingQuery.fulfilled, (state, action) => {
        const sessionId = action.meta.arg.sessionId;
        if (action.payload === null) {
          state.pendingQueries[sessionId] = null;
        }
        else {
          state.pendingQueries[sessionId] = action.payload;
        }
      })
      .addCase(cancelChatResponse.pending, (state) => {
        state.isCancellingQuery = true;
      })
      .addCase(cancelChatResponse.fulfilled, (state) => {
        state.isCancellingQuery = false;
      });
  },
});

export default sessionSlice.reducer;
